import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline';

import Alert from '../../components/Alert';
import InputGroup from '../../components/InputGroup';
import { AnchorDateSummary as AnchorDateSummaryType } from './anchorDates';
import { MAIL_SEQUENCE_ALERT_LINK_CLASS } from './scheduleMailFieldLabel';

interface Props {
  summary: AnchorDateSummaryType;
}

const clientAccountPath = (programInstanceId: string) =>
  `/clients/${programInstanceId}/account`;

const WhereToSetDates: FC<{
  settingFieldLabel: string;
  programInstanceId?: string;
}> = ({ settingFieldLabel, programInstanceId }) => (
  <p className="mt-1">
    {programInstanceId ? (
      <>
        Add {settingFieldLabel.toLowerCase()} on the client&apos;s{' '}
        <Link
          to={clientAccountPath(programInstanceId)}
          className={MAIL_SEQUENCE_ALERT_LINK_CLASS}
        >
          Account tab
        </Link>
        , or choose <strong>Specific date</strong>.
      </>
    ) : (
      <>
        Add {settingFieldLabel.toLowerCase()} on each client&apos;s Account tab,
        or choose <strong>Specific date</strong>.
      </>
    )}
  </p>
);

const LockedAnchorDateField: FC<{
  summary: Extract<
    AnchorDateSummaryType,
    { kind: 'single' } | { kind: 'shared' }
  >;
}> = ({ summary }) => (
  <div className="mt-3 space-y-3">
    <InputGroup
      type="date"
      label={summary.settingFieldLabel}
      useNaturalLettering
      readOnly
      inputSize="extra-small"
      containerClassName="w-full"
      value={summary.dateInputValue}
    />
    {summary.kind === 'shared' && (
      <Alert level="success" message={`Shared ${summary.label}`}>
        <p className="mt-1">
          All clients share the same {summary.label.toLowerCase()}:{' '}
          {summary.formattedDate}
        </p>
      </Alert>
    )}
  </div>
);

const AnchorDateSummary: FC<Props> = ({ summary }) => {
  const [expanded, setExpanded] = useState(false);

  if (summary.kind === 'no_recipients') {
    return (
      <Alert level="warning" message="Recipients Required">
        <p className="mt-1">Add recipients to see which dates will apply.</p>
      </Alert>
    );
  }

  if (summary.kind === 'single' || summary.kind === 'shared') {
    return <LockedAnchorDateField summary={summary} />;
  }

  if (summary.kind === 'none_set') {
    return (
      <Alert level="warning" message={`${summary.label} Not Set`}>
        <p className="mt-1">
          The {summary.label.toLowerCase()} isn&apos;t set for{' '}
          {summary.clientCount === 1
            ? 'this client'
            : `${summary.clientCount} clients`}
          .
        </p>
        <WhereToSetDates
          settingFieldLabel={summary.settingFieldLabel}
          programInstanceId={summary.programInstanceId}
        />
      </Alert>
    );
  }

  if (summary.kind === 'multiple') {
    return (
      <Alert level="warning" message={`Different ${summary.label}s`}>
        <p className="mt-1">
          Clients have different {summary.label.toLowerCase()}s (
          {summary.uniqueDateCount} unique). Each client&apos;s Mail timing uses
          their own date.
        </p>
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-left hover:text-orange-150"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span>View dates by client</span>
        </button>
        {expanded && (
          <ul className="border-orange-75/40 mt-2 space-y-1 border-l pl-5">
            {summary.entries.map((entry) => (
              <li key={entry.programInstanceId}>
                <Link
                  to={clientAccountPath(entry.programInstanceId)}
                  className={MAIL_SEQUENCE_ALERT_LINK_CLASS}
                >
                  {entry.name}
                </Link>
                {': '}
                {entry.formattedDate ?? 'Not set'}
              </li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }

  return null;
};

export default AnchorDateSummary;
